/**
 * Map Component
 * Unified Leaflet map integration with Alpine.js
 * Uses npm-bundled Leaflet with lazy loading for optimal performance
 */

import Alpine from 'alpinejs'
import type { Report, GeoLocation } from '../types'
import { L, createMarkerClusterGroup, isMarkerClusterAvailable } from '../lib/leaflet'

// ============================================================================
// Types
// ============================================================================

export interface MapOptions {
  center?: GeoLocation
  zoom?: number
  minZoom?: number
  maxZoom?: number
  enableClustering?: boolean
  enableSearch?: boolean
  onMarkerClick?: (report: Report) => void
  onMapClick?: (location: GeoLocation) => void
}

export interface MapMarker {
  id: string
  location: { latitude: number; longitude: number }
  report?: Report
  marker?: L.Marker
}

/** Nominatim search result */
interface NominatimResult {
  lat: string
  lon: string
  display_name: string
}

/** Nominatim reverse geocode result */
interface ReverseGeocodeResult {
  display_name: string
}

/** Container element with Leaflet properties */
interface LeafletContainer extends HTMLElement {
  _leaflet_id?: number
  _leaflet_map?: L.Map
}

// ============================================================================
// Shared Configuration
// ============================================================================

/**
 * Animal-type accent colors (POM low-poly tokens). Retained for popup accents
 * and any future consumer; not currently read by the marker renderer (markers
 * are driven by `condition` per D17).
 */
export const ANIMAL_COLORS: Record<string, string> = {
  dog: '#F6C94A', // Mustard
  cat: '#0F3A52', // Deep
  bird: '#EF7A5A', // Coral
  other: '#4FC3D6', // Teal
}

/**
 * Status color tokens (POM low-poly). Retained for any consumer that wants the
 * raw color; templates should prefer the `.pill-*` classes.
 */
export const STATUS_COLORS: Record<string, string> = {
  pending: '#F6C94A', // Mustard
  new: '#EF7A5A', // Coral
  investigating: '#0F3A52', // Deep
  resolved: '#A7E8D8', // Mint
  closed: '#ECDFC1', // Paper-2
}

/**
 * Marker glyph IDs by report condition (Decision D17).
 * `healthy` -> mustard, `injured`/`sick` -> coral, anything else -> deep-teal.
 */
function getMarkerSpriteId(condition: string | undefined): string {
  if (condition === 'healthy') {
    return 'i-marker-healthy'
  }
  if (condition === 'injured' || condition === 'sick') {
    return 'i-marker-injured'
  }
  return 'i-marker-unknown'
}

/**
 * Pill class for status badge inside marker popup.
 */
function getStatusPillClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'pill pill-pending'
    case 'new':
      return 'pill pill-new'
    case 'investigating':
      return 'pill pill-investigating'
    case 'resolved':
      return 'pill pill-resolved'
    case 'closed':
      return 'pill pill-closed'
    default:
      return 'pill'
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display address from report
 */
function getDisplayAddress(report: Report): string {
  const ld = report.locationDetails
  if (ld?.displayName) {
    return ld.displayName
  }
  if (report.manualLocation) {
    return report.manualLocation
  }

  const parts = [ld?.road, ld?.suburb, ld?.city, ld?.country].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : 'Location unknown'
}

/**
 * Get photo URL from report (handles both old and new formats)
 */
function getPhotoUrl(report: Report): string {
  return report.photoURL ?? report.photoURLs?.[0] ?? ''
}

/**
 * Format animal type for display
 */
function formatAnimalType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

/**
 * Format status for display
 */
function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

// ============================================================================
// Map Component
// ============================================================================

/**
 * Create a map component
 * This is the main component used on both find.html and admin.html
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function mapComponent(options: MapOptions = {}) {
  return {
    map: null as L.Map | null,
    markers: new Map<string, MapMarker>(),
    markerCluster: null as L.MarkerClusterGroup | null,
    userLocation: null as GeoLocation | null,
    loading: true,
    error: null as string | null,
    initialized: false,

    // Default options
    center: options.center ?? { latitude: 20, longitude: 0 },
    zoom: options.zoom ?? 3,
    minZoom: options.minZoom ?? 2,
    maxZoom: options.maxZoom ?? 18,
    enableClustering: options.enableClustering ?? true,

    /**
     * Initialize the map
     * Handles delayed initialization and container visibility
     */
    async init(): Promise<void> {
      // Prevent double initialization
      if (this.initialized) {
        console.warn('[Map] Already initialized, skipping')
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const refs = (this as any).$refs as Record<string, HTMLElement> | undefined
      const container = refs?.mapContainer as LeafletContainer | undefined

      if (!container) {
        this.error = 'Map container not found'
        this.loading = false
        return
      }

      // Wait for container to be visible and have dimensions
      await this.waitForContainer(container)

      try {
        // Check if container already has a map (HMR or re-initialization)
        if (container._leaflet_id) {
          const existingMap = container._leaflet_map
          if (existingMap) {
            existingMap.remove()
          } else {
            delete container._leaflet_id
          }
        }

        // Initialize map
        this.map = L.map(container, {
          center: [this.center.latitude, this.center.longitude],
          zoom: this.zoom,
          minZoom: this.minZoom,
          maxZoom: this.maxZoom,
          zoomControl: true,
          scrollWheelZoom: true,
        })

        // Store reference for cleanup
        container._leaflet_map = this.map

        // Add tile layer (OpenStreetMap)
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(this.map)

        // Initialize marker cluster if enabled and available
        if (this.enableClustering && isMarkerClusterAvailable()) {
          this.markerCluster = createMarkerClusterGroup()
          this.map.addLayer(this.markerCluster)
        }

        // Handle map click
        if (options.onMapClick) {
          this.map.on('click', (e: L.LeafletMouseEvent) => {
            options.onMapClick?.({
              latitude: e.latlng.lat,
              longitude: e.latlng.lng,
            })
          })
        }

        // Invalidate size after delays to ensure tiles load correctly
        this.invalidateSizeDelayed()

        this.initialized = true
        this.loading = false
        console.warn('[Map] Initialization complete')
      } catch (err) {
        this.error = 'Failed to initialize map'
        this.loading = false
        console.error('Map initialization error:', err)
      }
    },

    /**
     * Wait for container to be visible and have dimensions
     */
    async waitForContainer(container: HTMLElement): Promise<void> {
      return new Promise((resolve) => {
        const check = (): void => {
          const rect = container.getBoundingClientRect()
          if (rect.width > 0 && rect.height > 0) {
            resolve()
          } else {
            requestAnimationFrame(check)
          }
        }
        // Initial delay to let Alpine render
        setTimeout(check, 100)
      })
    },

    /**
     * Invalidate map size with delays for proper tile loading
     */
    invalidateSizeDelayed(): void {
      const invalidate = (): void => {
        if (this.map) {
          this.map.invalidateSize()
        }
      }
      setTimeout(invalidate, 100)
      setTimeout(invalidate, 300)
      setTimeout(invalidate, 500)
    },

    /**
     * Add a marker for a report
     */
    addMarker(report: Report): void {
      if (!this.map) {
        return
      }

      const { location } = report

      // Skip if no valid location
      if (
        !location ||
        typeof location.latitude !== 'number' ||
        typeof location.longitude !== 'number'
      ) {
        console.warn('[Map] Invalid location for report:', report.id)
        return
      }

      const lat = location.latitude
      const lng = location.longitude

      if (isNaN(lat) || isNaN(lng)) {
        console.warn('[Map] Invalid coordinates for report:', report.id)
        return
      }

      // Create custom icon
      const icon = this.createMarkerIcon(report)

      // Create marker
      const marker = L.marker([lat, lng], { icon })

      // Add popup with consistent content (opens on hover)
      marker.bindPopup(this.createPopupContent(report), {
        maxWidth: 280,
        className: 'custom-popup',
      })

      // Show popup on hover
      marker.on('mouseover', () => {
        marker.openPopup()
      })
      marker.on('mouseout', () => {
        marker.closePopup()
      })

      // Handle click - opens detail modal
      marker.on('click', () => {
        options.onMarkerClick?.(report)
      })

      // Add to cluster or map
      if (this.markerCluster) {
        this.markerCluster.addLayer(marker)
      } else {
        marker.addTo(this.map)
      }

      // Store reference
      this.markers.set(report.id, {
        id: report.id,
        location: { latitude: lat, longitude: lng },
        report,
        marker,
      })
    },

    /**
     * Remove a marker
     */
    removeMarker(id: string): void {
      const markerData = this.markers.get(id)
      if (!markerData?.marker) {
        return
      }

      if (this.markerCluster) {
        this.markerCluster.removeLayer(markerData.marker)
      } else if (this.map) {
        this.map.removeLayer(markerData.marker)
      }

      this.markers.delete(id)
    },

    /**
     * Update markers from reports
     */
    updateMarkers(reports: Report[]): void {
      if (!this.initialized || !this.map) {
        console.warn('[Map] Not ready, deferring marker update')
        return
      }

      // Remove old markers
      const newIds = new Set(reports.map((r) => r.id))
      for (const [id] of this.markers) {
        if (!newIds.has(id)) {
          this.removeMarker(id)
        }
      }

      // Add/update markers
      for (const report of reports) {
        if (!this.markers.has(report.id)) {
          this.addMarker(report)
        }
      }

      console.warn('[Map] Updated markers:', String(this.markers.size))
    },

    /**
     * Clear all markers
     */
    clearMarkers(): void {
      if (this.markerCluster) {
        this.markerCluster.clearLayers()
      } else {
        for (const [, markerData] of this.markers) {
          if (markerData.marker && this.map) {
            this.map.removeLayer(markerData.marker)
          }
        }
      }
      this.markers.clear()
    },

    /**
     * Create marker icon based on report condition (Decision D17).
     * Renders a faceted SVG teardrop from the sprite:
     *   healthy -> mustard, injured/sick -> coral, otherwise deep-teal.
     */
    createMarkerIcon(report: Report): L.DivIcon {
      const spriteId = getMarkerSpriteId(report.condition)

      return L.divIcon({
        className: 'custom-marker',
        html: `
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 40 52"
            width="40"
            height="52"
            aria-hidden="true"
            style="display: block; filter: drop-shadow(0 2px 4px rgba(15,58,82,0.25));"
          >
            <use href="/assets/icons.svg#${spriteId}" />
          </svg>
        `,
        iconSize: [40, 52],
        iconAnchor: [20, 52],
        popupAnchor: [0, -52],
      })
    },

    /**
     * Create popup content for a marker
     * Consistent HTML across all pages
     */
    createPopupContent(report: Report): string {
      const photoUrl = getPhotoUrl(report)
      const address = getDisplayAddress(report)
      const animalType = formatAnimalType(report.animalType)
      const status = formatStatus(report.status)
      const pillClass = getStatusPillClass(report.status)

      return `
        <div class="map-popup" style="min-width: 200px;">
          ${
            photoUrl
              ? `<img src="${photoUrl}" alt="${animalType}" style="width: 100%; height: 140px; object-fit: cover; border-radius: 8px; margin-bottom: 12px;">`
              : ''
          }
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; gap: 8px;">
            <h3 style="margin: 0; font-family: var(--font-display, 'Fraunces', serif); font-weight: 500; font-size: 18px; color: var(--pom-deep, #0F3A52);">${animalType}</h3>
            <span class="${pillClass}">${status}</span>
          </div>
          <p style="margin: 0; color: var(--ink-2, #5A6772); font-size: 13px; display: flex; align-items: flex-start; gap: 6px;">
            <svg class="icon icon-sm" aria-hidden="true" style="flex-shrink: 0;"><use href="/assets/icons.svg#i-location-on" /></svg>
            <span style="line-height: 1.4; color: var(--ink, #1A2530);">${address}</span>
          </p>
          ${report.caseId ? `<p style="margin: 8px 0 0; color: var(--ink-2, #5A6772); font-size: 11px; font-family: var(--font-mono, 'JetBrains Mono', monospace); letter-spacing: 0.12em; text-transform: uppercase;">Case #${report.caseId}</p>` : ''}
        </div>
      `
    },

    /**
     * Pan to a location
     */
    panTo(location: GeoLocation, zoom?: number): void {
      if (!this.map) {
        return
      }
      this.map.setView([location.latitude, location.longitude], zoom ?? this.zoom)
    },

    /**
     * Fit bounds to show all markers
     */
    fitBounds(): void {
      if (!this.map || this.markers.size === 0) {
        return
      }

      const bounds = L.latLngBounds([])

      for (const [, markerData] of this.markers) {
        bounds.extend([markerData.location.latitude, markerData.location.longitude])
      }

      // Fit bounds with padding and reasonable max zoom
      this.map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 12,
      })
    },

    /**
     * Get user's current location
     */
    async getUserLocation(): Promise<GeoLocation | null> {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location: GeoLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            }
            this.userLocation = location
            resolve(location)
          },
          () => {
            resolve(null)
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000,
          }
        )
      })
    },

    /**
     * Show user location on map
     */
    async showUserLocation(): Promise<void> {
      const location = await this.getUserLocation()
      if (!location || !this.map) {
        return
      }

      const userIcon = L.divIcon({
        className: 'user-location-marker',
        html: `
          <div style="
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background-color: #F6C94A;
            border: 3px solid #0F3A52;
            box-shadow: 0 2px 4px rgba(15,58,82,0.25);
          "></div>
        `,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      })

      L.marker([location.latitude, location.longitude], { icon: userIcon })
        .addTo(this.map)
        .bindPopup('Your Location')

      this.panTo(location, 14)
    },

    /**
     * Cleanup map resources
     */
    destroy(): void {
      if (this.map) {
        this.map.remove()
        this.map = null
      }
      this.markers.clear()
      this.initialized = false
    },
  }
}

// ============================================================================
// Location Picker Component
// ============================================================================

/**
 * Create a location picker component (for report form)
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function locationPicker(
  options: { onSelect?: (location: GeoLocation, address: string) => void } = {}
) {
  const baseMap = mapComponent({
    center: { latitude: 30, longitude: 10 },
    zoom: 3,
    enableClustering: false,
  })

  return {
    ...baseMap,

    selectedLocation: null as GeoLocation | null,
    selectedAddress: '',
    searchQuery: '',
    searching: false,
    searchResults: [] as NominatimResult[],
    searchDebounceTimer: null as ReturnType<typeof setTimeout> | null,

    /**
     * Initialize picker with click handler and visibility observer
     */
    async initPicker(): Promise<void> {
      await this.init()
      if (this.map) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const self = this as any as { selectLocation: (location: GeoLocation) => Promise<void> }
        this.map.on('click', (e: L.LeafletMouseEvent) => {
          void self.selectLocation({
            latitude: e.latlng.lat,
            longitude: e.latlng.lng,
          })
        })

        // Watch for container visibility changes (modal open/close)
        // to properly invalidate map size and load tiles
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        const refs = (this as any).$refs as Record<string, HTMLElement> | undefined
        const container = refs?.mapContainer
        if (container) {
          const map = this.map
          const observer = new ResizeObserver(() => {
            map.invalidateSize()
          })
          observer.observe(container)
        }
      }
    },

    /**
     * Select a location
     */
    async selectLocation(location: GeoLocation): Promise<void> {
      this.selectedLocation = location

      // Remove previous selected marker from the map
      const prev = this.markers.get('selected')
      if (prev?.marker && this.map) {
        this.map.removeLayer(prev.marker)
      }
      this.markers.delete('selected')

      if (this.map) {
        const selectedIcon = L.divIcon({
          className: 'selected-location-marker',
          html: `
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 64 64"
              width="40"
              height="40"
              aria-hidden="true"
              style="display: block; color: #EF7A5A; filter: drop-shadow(0 2px 4px rgba(15,58,82,0.25));"
            >
              <use href="/assets/icons.svg#i-location-on" />
            </svg>
          `,
          iconSize: [40, 40],
          iconAnchor: [20, 38],
        })

        const marker = L.marker([location.latitude, location.longitude], { icon: selectedIcon })
        marker.addTo(this.map)
        this.markers.set('selected', {
          id: 'selected',
          location: { latitude: location.latitude, longitude: location.longitude },
          marker,
        })
      }

      await this.reverseGeocode(location)
      options.onSelect?.(location, this.selectedAddress)
    },

    /**
     * Reverse geocode location to address
     */
    async reverseGeocode(location: GeoLocation): Promise<void> {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${String(location.latitude)}&lon=${String(location.longitude)}&addressdetails=1&zoom=18`
        )
        const data = (await response.json()) as ReverseGeocodeResult
        this.selectedAddress = data.display_name || 'Unknown location'
      } catch {
        this.selectedAddress = `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`
      }
    },

    /**
     * Handle search input with debounce for autocomplete
     */
    onSearchInput(): void {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }

      const query = this.searchQuery.trim()
      if (query.length < 3) {
        this.searchResults = []
        return
      }

      this.searchDebounceTimer = setTimeout(() => {
        void this.fetchSearchResults(query)
      }, 300)
    },

    /**
     * Fetch search results from Nominatim
     */
    async fetchSearchResults(query: string): Promise<void> {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`
        )
        this.searchResults = (await response.json()) as NominatimResult[]
      } catch {
        this.searchResults = []
      }
    },

    /**
     * Select a result from the autocomplete dropdown
     */
    async selectSearchResult(result: NominatimResult): Promise<void> {
      const location: GeoLocation = {
        latitude: parseFloat(result.lat),
        longitude: parseFloat(result.lon),
      }

      this.searchQuery = result.display_name
      this.searchResults = []
      this.panTo(location, 15)
      await this.selectLocation(location)
    },

    /**
     * Search for a location by query (search button / enter key)
     */
    async searchLocation(): Promise<void> {
      if (!this.searchQuery.trim()) {
        return
      }

      this.searching = true
      this.searchResults = []

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.searchQuery)}&limit=5&addressdetails=1`
        )
        const results = (await response.json()) as NominatimResult[]

        if (results.length > 0) {
          const result = results[0]
          const location: GeoLocation = {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
          }

          this.panTo(location, 15)
          await this.selectLocation(location)
        } else {
          this.error = 'No results found. Try a different search term.'
        }
      } catch (error) {
        console.error('Location search error:', error)
        this.error = 'Location search failed. Please try again.'
      } finally {
        this.searching = false
      }
    },

    /**
     * Use current location
     */
    async useCurrentLocation(): Promise<void> {
      const location = await this.getUserLocation()
      if (location) {
        this.panTo(location, 15)
        await this.selectLocation(location)
      }
    },
  }
}

// ============================================================================
// Register Components
// ============================================================================

export function registerMapComponents(): void {
  Alpine.data('map', mapComponent)
  Alpine.data('locationPicker', locationPicker)
}
